// Wyszukiwanie, tagi i ulubione w menu (etap 9/10). Czysta logika filtrowania,
// bez DOM-u i bez bazy - listę widoczną w menu buduje `main.ts`, ten moduł
// tylko decyduje, co z niej zostaje po wpisanym szukaniu i wybranych filtrach.
// Rozdzielone od `main.ts` z tego samego powodu co `store/plik.ts`: da się
// przetestować samym Vitestem, bez uruchamiania niczego graficznego.

import type { Utwor } from '../store/db.ts';

/** Prosta mapa polskich znaków diakrytycznych na ich odpowiedniki bez ogonków
 * i kropek - tyle, żeby "chalupa" znalazło "chałupa" bez ładowania pełnej
 * normalizacji Unicode. Tylko małe litery: wejście przechodzi przez
 * `toLowerCase()` PRZED tym mapowaniem. */
const MAPA_DIAKRYTYKOW: Record<string, string> = {
  ą: 'a',
  ć: 'c',
  ę: 'e',
  ł: 'l',
  ń: 'n',
  ó: 'o',
  ś: 's',
  ź: 'z',
  ż: 'z',
};

/** Małe litery + zdjęcie polskich diakrytyków, żeby "ą" w polu wyszukiwania
 * trafiało też w "a" w tytule/wykonawcy/tagu (i odwrotnie). */
function normalizuj(tekst: string): string {
  let wynik = '';
  for (const znak of tekst.toLowerCase()) {
    wynik += MAPA_DIAKRYTYKOW[znak] ?? znak;
  }
  return wynik;
}

/** Czy `utwor` pasuje do już znormalizowanego (małe litery, bez diakrytyków)
 * zapytania - dopasowanie w tytule, wykonawcy albo którymkolwiek tagu. Pusty
 * `znormalizowaneSzukane` pasuje do wszystkiego (brak wpisanego tekstu = brak
 * filtra). */
function pasujeDoSzukania(utwor: Utwor, znormalizowaneSzukane: string): boolean {
  if (znormalizowaneSzukane === '') return true;
  const pola = [utwor.tytul, utwor.wykonawca ?? '', ...(utwor.tagi ?? [])];
  return pola.some((pole) => normalizuj(pole).includes(znormalizowaneSzukane));
}

/** Filtruje `utwory` przez łańcuch szukaj -> ulubione -> tag, W TEJ KOLEJNOŚCI
 * i BEZ zmiany kolejności pozycji - lista wejściowa może być już w kolejności
 * setlisty (patrz `obliczWidoczneUtwory` w `main.ts`), a ta funkcja tylko
 * wybiera podzbiór, nigdy nie sortuje. */
export function filtrujUtwory(
  utwory: Utwor[],
  szukaj: string,
  tylkoUlubione: boolean,
  wybranyTag: string | null,
): Utwor[] {
  const znormalizowaneSzukane = normalizuj(szukaj.trim());
  return utwory
    .filter((utwor) => pasujeDoSzukania(utwor, znormalizowaneSzukane))
    .filter((utwor) => !tylkoUlubione || utwor.ulubiony === true)
    .filter((utwor) => wybranyTag === null || (utwor.tagi ?? []).includes(wybranyTag));
}

/** Zbiera wszystkie unikatowe tagi z `utwory`, alfabetycznie (polskie
 * sortowanie), do wyświetlenia jako rząd „chipów" w menu. */
export function zbierzTagi(utwory: Utwor[]): string[] {
  const zbior = new Set<string>();
  for (const utwor of utwory) {
    for (const tag of utwor.tagi ?? []) zbior.add(tag);
  }
  return [...zbior].sort((a, b) => a.localeCompare(b, 'pl'));
}
